/**
 * Whether the owner can talk to a real Worker from Main Command right now, and by
 * which existing route (#511, Phase 1 - isolated foundation).
 *
 * Pure: rows in, one truthful answer per Worker out. No I/O, no clock, no write,
 * and nothing here sends anything. It maps the canonical chain
 *
 *   Worker (brunel.workers)
 *     -> machine (the live terminal's node, else the workforce session's node)
 *     -> current workforce session (brunel.sessions, unretired, lifecycle)
 *     -> the one live Worker terminal (brunel.worker_terminals)
 *     -> the persistent AI session riding on it (node `persistent-ai-state` snapshot)
 *     -> runtime / provider / model (the session's offering)
 *     -> conversational availability
 *
 * and classifies it with an exact reason. The rules it holds:
 *
 * - One route only: a bounded turn into the Worker's EXISTING persistent AI session
 *   (node `PersistentAiSessionService`, which writes through the same authoritative
 *   terminal input hold every dispatched instruction uses). No second PTY, no hidden
 *   exec, no planner, no second message store.
 * - Never the owner's keyboard: a terminal the owner is holding is not written to.
 * - Never a substitute runtime: the turn goes to the runtime/model the Worker's
 *   session was dispatched on, or not at all.
 * - Never into a turn in flight: the node's send path has no single-flight guard
 *   today, so a Worker with an active task or a busy turn is "cannot interleave" -
 *   the chat turn may only wait behind it, never be typed into it.
 * - Unknown stays Unknown: a Worker whose conversational state has not been heard
 *   is not called available.
 */

import type {
  ModelOfferingRef, NodeOffering, NodePresence, RuntimeOutage, Worker, WorkerTerminalRow, WorkforceSession,
} from './data';
import { connectionState, lastSeenLabel, type OperationalStatus } from './presence';
import { persistentRuntimeLabel } from './persistent-ai-session-view';

/** The node's `persistent-ai-state` broadcast, as the browser receives it. */
export interface PersistentAiStateSnapshot {
  /** The registry key: the workforce session id, or (older nodes) the Worker id. */
  readonly workerId: string;
  readonly sessionId: string;
  readonly runtimeKey: string;
  readonly modelKey?: string | null;
  readonly state: string;
  /** Existing canonical Worker terminal id for the PTY path. */
  readonly processId?: string | null;
  readonly taskId?: string | null;
  readonly runId?: string | null;
  /** A finished task is not safe to chat over until its result/handoff is durable. */
  readonly durableResultAt?: number | null;
  readonly lastActivityAt?: number | null;
  readonly supported?: boolean;
  readonly unsupportedReason?: string | null;
}

export type WorkerChatAvailability =
  | 'available'
  | 'busy'
  | 'waiting'
  | 'blocked'
  | 'stale'
  | 'retired'
  | 'runtime_unavailable'
  | 'terminal_unavailable'
  | 'no_conversational_session'
  | 'cannot_interleave';

/** The exact reason, as a stable code. Every code has one owner-facing sentence. */
export type WorkerChatReason =
  | 'ready'
  | 'no_workforce_session'
  | 'worker_retired'
  | 'session_retired'
  | 'session_in_handoff'
  | 'worker_offline'
  | 'worker_rotating'
  | 'worker_blocked'
  | 'machine_unknown'
  | 'machine_heartbeat_late'
  | 'machine_out_of_touch'
  | 'no_live_terminal'
  | 'terminal_ended'
  | 'terminal_not_ready'
  | 'no_ai_session'
  | 'conversational_state_unknown'
  | 'runtime_not_persistent'
  | 'runtime_offering_missing'
  | 'runtime_not_offered_on_machine'
  | 'runtime_outage'
  | 'runtime_mismatch'
  | 'model_mismatch'
  | 'ai_session_error'
  | 'ai_context_retired'
  | 'ai_session_closed'
  | 'ai_session_starting'
  | 'owner_has_keyboard'
  | 'input_held_by_controller'
  | 'terminal_awaiting_approval'
  | 'terminal_waiting_for_input'
  | 'worker_awaiting_approval'
  | 'active_task_in_flight'
  | 'turn_in_flight'
  | 'terminal_session_mismatch'
  | 'result_not_durable'
  | 'no_runtime_run';

/** When a bounded chat turn could be delivered, if at all. */
export type WorkerChatDelivery = 'now' | 'after_current_turn' | 'not_possible';

/**
 * The one existing canonical route a bounded conversational turn would take.
 * Present only for a Worker whose chain resolves end to end; null otherwise.
 */
export interface WorkerChatRoute {
  readonly kind: 'persistent_session_turn';
  readonly method: 'WorkerRuntimeAdapter.sendMessage';
  readonly workerId: string;
  readonly workforceSessionId: string;
  readonly nodeId: string;
  readonly terminalSessionId: string;
  readonly persistentSessionId: string;
  /** Existing runtime-adapter run handle required by WorkerRuntimeAdapter.sendMessage. */
  readonly runId: string;
  /** Exactly the runtime/model the session was dispatched on - never a substitute. */
  readonly runtimeKey: string;
  readonly modelKey: string | null;
  readonly delivery: Exclude<WorkerChatDelivery, 'not_possible'>;
}

export interface WorkerChatReadiness {
  /** Stable recipient identity: the Worker's own id, never a session or terminal id. */
  readonly recipientKey: string;
  readonly workerId: string;
  readonly workerNumber: number;
  readonly workerName: string;
  /** "Worker 1 · Builder" - the attribution a reply carries. */
  readonly label: string;
  readonly workerStatus: string;

  readonly nodeId: string | null;
  readonly nodeName: string | null;
  readonly machineConnection: 'connected' | 'stale' | 'disconnected' | 'unknown';

  readonly workforceSessionId: string | null;
  readonly lifecycle: string | null;
  readonly projectId: string | null;
  readonly projectName: string | null;

  readonly terminalSessionId: string | null;
  readonly terminalStatus: string | null;
  readonly inputHolder: string | null;

  readonly persistentSessionId: string | null;
  readonly persistentState: string | null;

  readonly offeringKey: string | null;
  readonly runtimeKey: string | null;
  readonly runtimeLabel: string | null;
  readonly modelKey: string | null;
  readonly providerLabel: string | null;

  readonly activeTaskId: string | null;
  readonly activeTaskTitle: string | null;

  readonly availability: WorkerChatAvailability;
  readonly reason: WorkerChatReason;
  /** One sentence, in the owner's words, naming exactly why. */
  readonly reasonText: string;
  readonly delivery: WorkerChatDelivery;
  readonly route: WorkerChatRoute | null;
  /**
   * Whether this Worker may appear as a participant chip. A retired or stale
   * session is never offered as someone to talk to.
   */
  readonly listed: boolean;
}

export interface WorkerChatReadinessInput {
  readonly nowMs: number;
  readonly workers: readonly Worker[];
  readonly workerTerminals: readonly WorkerTerminalRow[];
  readonly workforceSessions: readonly WorkforceSession[];
  readonly nodes: readonly NodePresence[];
  readonly nodeOfferings: readonly NodeOffering[];
  readonly modelOfferings: readonly ModelOfferingRef[];
  readonly runtimeOutages: readonly RuntimeOutage[];
  /** Newest `persistent-ai-state` snapshot per registry key, as heard on the node channel. */
  readonly persistentSessions: readonly PersistentAiStateSnapshot[];
  /**
   * The node ids whose terminal channel has actually been heard. A node not in this
   * set has told us nothing about its persistent sessions, so their absence is
   * Unknown rather than "none".
   */
  readonly heardNodes: ReadonlySet<string>;
}

const CURRENT_LIFECYCLES: ReadonlySet<string> = new Set(['active', 'restored']);
const HANDOFF_LIFECYCLES: ReadonlySet<string> = new Set([
  'handoff_preparing', 'handoff_ready', 'replacement_started', 'replacement_verified',
]);
const OWNER_INPUT: ReadonlySet<string> = new Set(['owner_terminal', 'owner_composer']);

/** The Main Command recipient key for a Worker. Built on the stable Worker id only. */
export function workerRecipientKey(workerId: string): string {
  return `worker:${workerId}`;
}

/** The Worker id back out of a recipient key, or null for anything that is not one. */
export function workerIdFromRecipientKey(key: string): string | null {
  if (!key.startsWith('worker:')) return null;
  const id = key.slice('worker:'.length);
  return id === '' ? null : id;
}

/** "Worker 1 · Builder", or "Worker 1" when the name is only the number again. */
export function workerChatLabel(number: number, name: string): string {
  const trimmed = name.trim();
  return trimmed === '' || trimmed === `Worker ${number}` ? `Worker ${number}` : `Worker ${number} · ${trimmed}`;
}

/**
 * The byline a Worker's reply carries in the shared Main Command timeline:
 * who answered, on which machine, through which runtime/model.
 */
export function workerReplyByline(readiness: Pick<
  WorkerChatReadiness, 'label' | 'nodeName' | 'runtimeLabel' | 'modelKey'
>): string {
  const runtime = readiness.runtimeLabel === null
    ? null
    : readiness.modelKey === null ? readiness.runtimeLabel : `${readiness.runtimeLabel} / ${readiness.modelKey}`;
  return [readiness.label, readiness.nodeName, runtime].filter((part): part is string => part !== null).join(' · ');
}

function reasonSentence(reason: WorkerChatReason, ctx: {
  readonly name: string;
  readonly nodeName: string | null;
  readonly lastSeen: string | null;
  readonly runtime: string | null;
  readonly liveRuntime: string | null;
  readonly model: string | null;
  readonly liveModel: string | null;
  readonly holder: string | null;
  readonly task: string | null;
  readonly unsupported: string | null;
  readonly lifecycle: string | null;
}): string {
  const machine = ctx.nodeName ?? 'its machine';
  switch (reason) {
    case 'ready':
      return `${ctx.name} is at rest in its ${ctx.runtime ?? 'AI'} session and can answer now.`;
    case 'no_workforce_session':
      return `${ctx.name} has no current session on any machine, so there is no one to ask.`;
    case 'worker_retired':
      return `${ctx.name} was discarded. Its history is kept; it is no longer an active Worker.`;
    case 'session_retired':
      return `${ctx.name}'s session has been retired.`;
    case 'session_in_handoff':
      return `${ctx.name}'s session is being handed over (${(ctx.lifecycle ?? 'handoff').replaceAll('_', ' ')}); a question now would land in the outgoing context.`;
    case 'worker_offline':
      return `${ctx.name} is offline.`;
    case 'worker_rotating':
      return `${ctx.name} is rotating to a new session.`;
    case 'worker_blocked':
      return `${ctx.name} is blocked.`;
    case 'machine_unknown':
      return `The machine ${ctx.name} is on has not reported, so whether it can answer is Unknown.`;
    case 'machine_heartbeat_late':
      return `${machine} has not checked in since ${ctx.lastSeen ?? 'an unknown time'}; ${ctx.name} may not be reachable.`;
    case 'machine_out_of_touch':
      return `${machine} is out of touch (last seen ${ctx.lastSeen ?? 'never'}).`;
    case 'no_live_terminal':
      return `${ctx.name} has no live Worker terminal, so there is no session to talk to.`;
    case 'terminal_ended':
      return `${ctx.name}'s terminal has ended.`;
    case 'terminal_not_ready':
      return `${ctx.name}'s canonical terminal has not reached an idle conversational state yet.`;
    case 'no_ai_session':
      return `${machine} reports no AI session on ${ctx.name}'s terminal; there is no conversation to join until it is given work.`;
    case 'conversational_state_unknown':
      return `${machine} has not reported ${ctx.name}'s AI session, so whether it can hold a conversation is Unknown.`;
    case 'runtime_not_persistent':
      return ctx.unsupported ?? `${ctx.runtime ?? 'This runtime'} does not keep a conversation open between turns.`;
    case 'runtime_offering_missing':
      return `${ctx.name}'s pinned runtime/model offering is not in the current registry.`;
    case 'runtime_not_offered_on_machine':
      return `${machine} is not currently offering ${ctx.runtime ?? 'the pinned runtime/model'}.`;
    case 'runtime_outage':
      return `${ctx.runtime ?? 'The pinned runtime'} is currently unavailable on ${machine}.`;
    case 'runtime_mismatch':
      return `${ctx.name}'s live AI session is ${ctx.liveRuntime ?? 'a different runtime'}, not the ${ctx.runtime ?? 'dispatched runtime'} its session was given; Brunel will not answer through a different runtime.`;
    case 'model_mismatch':
      return `${ctx.name}'s live AI session runs ${ctx.liveModel ?? 'a different model'}, not ${ctx.model ?? 'the dispatched model'}; Brunel will not answer through a different model.`;
    case 'ai_session_error':
      return `${ctx.name}'s AI session is in error.`;
    case 'ai_context_retired':
      return `${ctx.name}'s AI conversation was retired; it has no context to answer from until its next task.`;
    case 'ai_session_closed':
      return `${ctx.name}'s AI session is closed.`;
    case 'ai_session_starting':
      return `${ctx.name}'s AI session is still starting; a question would wait until it is ready.`;
    case 'owner_has_keyboard':
      return `You have ${ctx.name}'s keyboard. Brunel will not type into a terminal you are holding.`;
    case 'input_held_by_controller':
      return `${ctx.holder ?? 'Another controller'} has ${ctx.name}'s keyboard; a question would wait until it is released.`;
    case 'terminal_awaiting_approval':
      return `${ctx.name}'s tool is asking for permission; anything typed now would be taken as the answer.`;
    case 'terminal_waiting_for_input':
      return `${ctx.name}'s tool is waiting for input; anything typed now would be taken as the answer.`;
    case 'worker_awaiting_approval':
      return `${ctx.name} is waiting for an approval.`;
    case 'active_task_in_flight':
      return `${ctx.name} is working on "${ctx.task ?? 'a task'}"; a question cannot be typed into that turn and would wait until it finishes.`;
    case 'turn_in_flight':
      return `${ctx.name} is in the middle of a turn; a question would wait until it finishes.`;
    case 'terminal_session_mismatch':
      return `${ctx.name}'s AI conversation is attached to a different terminal; Brunel will not guess which session to type into.`;
    case 'result_not_durable':
      return `${ctx.name}'s previous task result/handoff is not durable yet, so a chat turn is not safe.`;
    case 'no_runtime_run':
      return `${ctx.name}'s AI conversation has no current runtime run handle for a bounded follow-up.`;
  }
}

function runtimeOutageActive(
  nodeId: string,
  runtimeKey: string,
  outages: readonly RuntimeOutage[],
  nowMs: number,
): boolean {
  return outages.some((item) => {
    if (item.node_id !== nodeId || item.runtime_key !== runtimeKey || item.expired) return false;
    if (item.available_from === null) return true;
    const availableAt = Date.parse(item.available_from);
    return !Number.isFinite(availableAt) || availableAt > nowMs;
  });
}

/**
 * Resolve the Worker's current canonical workforce session independently of its
 * terminal view. A missing/stale terminal row must never make a real active
 * brunel.sessions row disappear or turn an old session into the current one.
 */
function currentWorkforceSession(
  sessions: readonly WorkforceSession[],
  rows: readonly WorkerTerminalRow[],
  workerId: string,
  snapshotKeys: ReadonlySet<string>,
): WorkforceSession | null {
  const candidates = sessions.filter((session) => session.worker_id === workerId);
  if (candidates.length === 0) return null;
  const rank = (session: WorkforceSession): number => {
    const current = CURRENT_LIFECYCLES.has(session.lifecycle);
    const hasSnapshot = snapshotKeys.has(session.id);
    const hasTerminal = rows.some((row) => row.workforce_session_id === session.id);
    if (current && hasSnapshot && hasTerminal) return 0;
    if (current && hasSnapshot) return 1;
    if (current && hasTerminal) return 2;
    if (current) return 3;
    if (HANDOFF_LIFECYCLES.has(session.lifecycle)) return 4;
    if (session.lifecycle === 'retired') return 6;
    return 5;
  };
  return [...candidates].sort((a, b) => rank(a) - rank(b) || a.id.localeCompare(b.id))[0]!;
}

/** The canonical terminal for exactly the selected workforce session, never an older one. */
function currentRow(rows: readonly WorkerTerminalRow[], workforceSessionId: string): WorkerTerminalRow | null {
  const matches = rows.filter((row) => row.workforce_session_id === workforceSessionId);
  if (matches.length === 0) return null;
  const rank = (row: WorkerTerminalRow): number => {
    const ended = row.closed_at !== null || row.failure !== null
      || ['exited', 'error', 'closed', 'failed'].includes(row.terminal_status ?? '');
    if (row.session_id !== null && !ended) return 0;
    if (row.session_id !== null) return 1;
    return 2;
  };
  return [...matches].sort((a, b) => rank(a) - rank(b)
    || (a.session_id ?? '').localeCompare(b.session_id ?? ''))[0]!;
}

function snapshotFor(
  snapshots: readonly PersistentAiStateSnapshot[],
  workforceSessionId: string | null,
  workerId: string,
): PersistentAiStateSnapshot | null {
  // The registry keys by workforce session id; an older node keyed by Worker id.
  return (workforceSessionId === null ? undefined : snapshots.find((item) => item.workerId === workforceSessionId))
    ?? snapshots.find((item) => item.workerId === workerId)
    ?? null;
}

/** Every Worker's conversational readiness, in Worker-number order. */
export function workerChatReadiness(input: WorkerChatReadinessInput): readonly WorkerChatReadiness[] {
  const snapshotKeys = new Set(input.persistentSessions.map((item) => item.workerId));
  return [...input.workers]
    .sort((a, b) => a.number - b.number)
    .map((worker) => readinessFor(input, worker, snapshotKeys));
}

function readinessFor(
  input: WorkerChatReadinessInput,
  worker: Worker,
  snapshotKeys: ReadonlySet<string>,
): WorkerChatReadiness {
  const workerRows = input.workerTerminals.filter((item) => item.worker_id === worker.id);
  const workforce = currentWorkforceSession(input.workforceSessions, workerRows, worker.id, snapshotKeys);
  const row = workforce === null ? null : currentRow(workerRows, workforce.id);
  const nodeId = workforce?.node_id ?? row?.node_id ?? null;
  const node = nodeId === null ? null : input.nodes.find((item) => item.node_id === nodeId) ?? null;
  const machineConnection = node === null
    ? 'unknown'
    : connectionState({
      lastSeenAt: node.last_seen_at, status: node.status as OperationalStatus, nowMs: input.nowMs,
    });
  const offeringKey = workforce?.offering_key ?? row?.offering_key ?? null;
  const offering = offeringKey === null ? null : input.modelOfferings.find((item) => item.key === offeringKey) ?? null;
  const snapshot = snapshotFor(input.persistentSessions, workforce?.id ?? null, worker.id);
  const runtimeKey = offering?.runtime_key ?? snapshot?.runtimeKey ?? null;
  const modelKey = offering?.model_key ?? snapshot?.modelKey ?? null;
  const label = workerChatLabel(worker.number, worker.name);
  const lifecycle = workforce?.lifecycle ?? row?.lifecycle ?? null;
  const workerStatus = row?.worker_status ?? worker.status;

  const base = {
    recipientKey: workerRecipientKey(worker.id),
    workerId: worker.id,
    workerNumber: worker.number,
    workerName: worker.name,
    label,
    workerStatus,
    nodeId,
    nodeName: row?.node_name ?? node?.name ?? null,
    machineConnection,
    workforceSessionId: workforce?.id ?? null,
    lifecycle,
    projectId: workforce?.project_id ?? row?.project_id ?? null,
    projectName: row?.project_name ?? null,
    terminalSessionId: row?.session_id ?? null,
    terminalStatus: row?.terminal_status ?? null,
    inputHolder: row?.input_held === true ? row.input_holder : null,
    persistentSessionId: snapshot?.sessionId ?? null,
    persistentState: snapshot?.state ?? null,
    offeringKey,
    runtimeKey,
    runtimeLabel: runtimeKey === null ? null : persistentRuntimeLabel(runtimeKey),
    modelKey,
    providerLabel: offering?.provider_label ?? null,
    activeTaskId: row?.task_id ?? null,
    activeTaskTitle: row?.task_title ?? null,
  } as const;

  const ctx = {
    name: label,
    nodeName: base.nodeName,
    lastSeen: node === null ? null : lastSeenLabel(node.last_seen_at, input.nowMs),
    runtime: base.runtimeLabel,
    liveRuntime: snapshot === null ? null : persistentRuntimeLabel(snapshot.runtimeKey),
    model: offering?.model_key ?? null,
    liveModel: snapshot?.modelKey ?? null,
    holder: row?.input_holder_controller_name?.trim() || row?.input_holder?.replaceAll('_', ' ') || null,
    task: base.activeTaskTitle,
    unsupported: snapshot?.unsupportedReason ?? null,
    lifecycle,
  };

  const verdict = (
    availability: WorkerChatAvailability,
    reason: WorkerChatReason,
    delivery: WorkerChatDelivery,
    listed = true,
  ): WorkerChatReadiness => {
    // The current system has no safe queue/claim ingress yet. A concrete execution
    // route is exposed only when it can be sent NOW; waiting/busy states describe
    // readiness but deliberately carry no callable route.
    const route: WorkerChatRoute | null = delivery !== 'now'
      || base.workforceSessionId === null || nodeId === null || base.terminalSessionId === null
      || snapshot === null || runtimeKey === null || snapshot.runId == null
      ? null
      : {
        kind: 'persistent_session_turn',
        method: 'WorkerRuntimeAdapter.sendMessage',
        workerId: worker.id,
        workforceSessionId: base.workforceSessionId,
        nodeId,
        terminalSessionId: base.terminalSessionId,
        persistentSessionId: snapshot.sessionId,
        runId: snapshot.runId,
        runtimeKey,
        modelKey,
        delivery,
      };
    return {
      ...base,
      availability,
      reason,
      reasonText: reasonSentence(reason, ctx),
      delivery,
      route,
      listed,
    };
  };

  // 0. #650 a discarded (retired) Worker is never an active Worker or a recipient.
  if (worker.retired_at) return verdict('retired', 'worker_retired', 'not_possible', false);
  // 1. Is there a current session at all? Retired and absent are not offered as participants.
  if (workforce === null) {
    return verdict('no_conversational_session', 'no_workforce_session', 'not_possible', false);
  }
  if (lifecycle === 'retired') return verdict('retired', 'session_retired', 'not_possible', false);
  if (lifecycle !== null && HANDOFF_LIFECYCLES.has(lifecycle)) {
    return verdict('blocked', 'session_in_handoff', 'not_possible');
  }

  // 2. Is the Worker itself and its machine reachable? Stale is not offered either.
  if (workerStatus === 'offline') return verdict('stale', 'worker_offline', 'not_possible', false);
  if (machineConnection === 'unknown') return verdict('stale', 'machine_unknown', 'not_possible', false);
  if (machineConnection === 'disconnected') return verdict('stale', 'machine_out_of_touch', 'not_possible', false);
  if (machineConnection === 'stale') return verdict('stale', 'machine_heartbeat_late', 'not_possible', false);
  if (workerStatus === 'rotating') return verdict('blocked', 'worker_rotating', 'not_possible');

  // 3. Is there one live Worker terminal to carry the session?
  if (row === null || row.session_id === null) return verdict('terminal_unavailable', 'no_live_terminal', 'not_possible');
  if (row.closed_at !== null || row.failure !== null
    || ['exited', 'error', 'closed', 'failed'].includes(row.terminal_status ?? '')) {
    return verdict('terminal_unavailable', 'terminal_ended', 'not_possible');
  }
  if (row.terminal_status === null || ['requested', 'starting', 'opening'].includes(row.terminal_status)) {
    return verdict('terminal_unavailable', 'terminal_not_ready', 'not_possible');
  }

  // 4. Is the exact pinned runtime/model currently available on this machine?
  if (offeringKey === null || offering === null) {
    return verdict('runtime_unavailable', 'runtime_offering_missing', 'not_possible');
  }
  if (nodeId === null || !input.nodeOfferings.some((item) =>
    item.node_id === nodeId && item.offering_key === offeringKey)) {
    return verdict('runtime_unavailable', 'runtime_not_offered_on_machine', 'not_possible');
  }
  if (nodeId !== null && runtimeOutageActive(nodeId, offering.runtime_key, input.runtimeOutages, input.nowMs)) {
    return verdict('runtime_unavailable', 'runtime_outage', 'not_possible');
  }

  // 5. Is there a persistent AI session on that exact terminal/runtime/model?
  if (snapshot === null) {
    return nodeId !== null && input.heardNodes.has(nodeId)
      ? verdict('no_conversational_session', 'no_ai_session', 'not_possible')
      : verdict('no_conversational_session', 'conversational_state_unknown', 'not_possible');
  }
  if (snapshot.supported === false) return verdict('runtime_unavailable', 'runtime_not_persistent', 'not_possible');
  if (snapshot.processId !== row.session_id) {
    return verdict('terminal_unavailable', 'terminal_session_mismatch', 'not_possible');
  }
  if (snapshot.runtimeKey !== offering.runtime_key) {
    return verdict('runtime_unavailable', 'runtime_mismatch', 'not_possible');
  }
  if (snapshot.modelKey !== offering.model_key) {
    return verdict('runtime_unavailable', 'model_mismatch', 'not_possible');
  }
  switch (snapshot.state) {
    case 'error': return verdict('runtime_unavailable', 'ai_session_error', 'not_possible');
    case 'retired': return verdict('no_conversational_session', 'ai_context_retired', 'not_possible');
    case 'closed': return verdict('no_conversational_session', 'ai_session_closed', 'not_possible');
    default: break;
  }

  // 6. Keyboard authority: the owner's hold is never taken; a machine's hold is waited out.
  if (row.input_held === true && row.input_holder !== null) {
    if (OWNER_INPUT.has(row.input_holder)) return verdict('blocked', 'owner_has_keyboard', 'not_possible');
    return verdict('busy', 'input_held_by_controller', 'after_current_turn');
  }

  // 7. A prompt on screen would swallow a chat turn as its answer.
  if (row.terminal_status === 'waiting_for_approval') {
    return verdict('waiting', 'terminal_awaiting_approval', 'after_current_turn');
  }
  if (row.terminal_status === 'waiting_for_input' || snapshot.state === 'waiting_for_input') {
    return verdict('waiting', 'terminal_waiting_for_input', 'after_current_turn');
  }
  if (workerStatus === 'awaiting_approval') return verdict('waiting', 'worker_awaiting_approval', 'after_current_turn');
  if (workerStatus === 'blocked') return verdict('blocked', 'worker_blocked', 'not_possible');

  // 8. Never interleave with an active task or a turn in flight.
  if (row.task_id !== null || (snapshot.taskId != null && snapshot.state === 'busy')) {
    return verdict('cannot_interleave', 'active_task_in_flight', 'after_current_turn');
  }
  if (snapshot.state === 'busy' || row.terminal_status === 'running') {
    return verdict('busy', 'turn_in_flight', 'after_current_turn');
  }
  if (snapshot.state === 'starting') return verdict('waiting', 'ai_session_starting', 'after_current_turn');

  // A persistent turn may already be "ready" before the executor has made the
  // previous task result/handoff durable. Do not let a chat turn race that gap.
  if (snapshot.taskId != null && snapshot.durableResultAt == null) {
    return verdict('cannot_interleave', 'result_not_durable', 'not_possible');
  }
  if (snapshot.runId == null) {
    return verdict('no_conversational_session', 'no_runtime_run', 'not_possible');
  }

  // 9. At rest, on its own runtime, keyboard free, nothing in flight.
  if (snapshot.state === 'ready' && row.terminal_status === 'idle') {
    return verdict('available', 'ready', 'now');
  }
  return verdict('no_conversational_session', 'conversational_state_unknown', 'not_possible');
}

/**
 * Current active Worker choices use the isolated #562 projection. Historical
 * consumers continue to use the full readiness collection above.
 */
export { activeWorkerParticipants as workerChatParticipants } from './active-worker-projection';
